import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ArrowLeft, Plus, Download, Ban, QrCode as QrCodeIcon, ImageDown } from "lucide-react";
import { useDateFormat } from '@/contexts/DateFormatContext';
import { toast } from "sonner";

export default function ProjectAccessCodes() {
  const params = useParams();
  const projectId = parseInt(params.id || "0");
  const [, navigate] = useLocation();
  const { t } = useTranslation();

  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [invalidateDialogOpen, setInvalidateDialogOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState<any>(null);
  const [count, setCount] = useState(10);
  const { formatDateTime } = useDateFormat();

  const { data: project } = trpc.projects.getById.useQuery({ id: projectId });
  const { data: codes, refetch } = trpc.accessCodes.list.useQuery({ projectId });
  const { data: stats } = trpc.accessCodes.getStats.useQuery({ projectId });
  
  const generateMutation = trpc.accessCodes.generate.useMutation({
    onSuccess: () => {
      refetch();
      setGenerateDialogOpen(false);
      toast.success(t.accessCodes.codesGenerated);
    },
  });

  const invalidateMutation = trpc.accessCodes.invalidate.useMutation({
    onSuccess: () => {
      refetch();
      setInvalidateDialogOpen(false);
      toast.success(t.accessCodes.codeInvalidated);
    },
  });

  const downloadPDFMutation = trpc.pdf.generateAccessCodesPDF.useMutation({
    onSuccess: (data) => {
      // Convert base64 to blob and download
      const byteCharacters = atob(data.pdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `access-codes-${project?.projectNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(t.accessCodes.pdfGenerated);
    },
  });

  const downloadQRCodeMutation = trpc.accessCodes.getQRCode.useMutation({
    onSuccess: (data) => {
      // Convert base64 to blob and download as PNG
      const byteCharacters = atob(data.png);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-code-${data.projectNumber}-${data.code}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(t.accessCodes.imageDownloaded);
    },
  });

  const handleGenerate = () => {
    generateMutation.mutate({ projectId, count });
  };

  const handleInvalidate = () => {
    if (selectedCode) {
      invalidateMutation.mutate({ id: selectedCode.id });
    }
  };

  const handleDownloadPDF = () => {
    downloadPDFMutation.mutate({ projectId });
  };

  const handleDownloadQRCode = (codeId: number) => {
    downloadQRCodeMutation.mutate({ id: codeId });
  };

  if (!project) {
    return <div className="p-8">{t.common.loading}</div>;
  }

  return (
    <div className="container mx-auto p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate("/projects")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t.common.back}
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{project.title}</h1>
            <p className="text-muted-foreground">
              {t.accessCodes.title} - {codes?.length || 0}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleDownloadPDF}
            disabled={!codes || codes.length === 0 || downloadPDFMutation.isPending}
          >
            <Download className="h-4 w-4 mr-2" />
            {downloadPDFMutation.isPending ? t.common.loading : t.accessCodes.downloadPDF}
          </Button>
          <Button onClick={() => setGenerateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t.accessCodes.generateCodes}
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">{t.accessCodes.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalCodes}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">{t.accessCodes.active}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalCodes - stats.invalidatedCodes}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">{t.accessCodes.used}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.usedCodes}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Codes Table */}
      {codes && codes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <QrCodeIcon className="h-16 w-16 text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">{t.accessCodes.noCodes}</p>
            <Button onClick={() => setGenerateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t.accessCodes.generateFirstCodes}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>{t.accessCodes.title}</CardTitle>
            <CardDescription>{t.accessCodes.printInstructions}</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.accessCodes.code}</TableHead>
                  <TableHead>{t.accessCodes.usageCount}</TableHead>
                  <TableHead>{t.accessCodes.lastUsed}</TableHead>
                  <TableHead>{t.common.status}</TableHead>
                  <TableHead className="text-right">{t.common.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes?.map((code) => (
                  <TableRow key={code.id}>
                    <TableCell className="font-mono font-bold">{code.code}</TableCell>
                    <TableCell>{code.usageCount}</TableCell>
                    <TableCell>
                      {code.lastUsedAt ? formatDateTime(code.lastUsedAt) : "-"}
                    </TableCell>
                    <TableCell>
                      {!code.isInvalidated ? (
                        <span className="text-green-600">{t.accessCodes.active}</span>
                      ) : (
                        <span className="text-red-600">{t.accessCodes.invalidated}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadQRCode(code.id)}
                          disabled={downloadQRCodeMutation.isPending}
                          title={t.accessCodes.downloadImage}
                        >
                          <ImageDown className="h-4 w-4" />
                        </Button>
                        {!code.isInvalidated && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedCode(code);
                              setInvalidateDialogOpen(true);
                            }}
                          >
                            <Ban className="h-4 w-4 mr-2" />
                            {t.accessCodes.invalidate}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Generate Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.accessCodes.generateCodes}</DialogTitle>
            <DialogDescription>
              {t.accessCodes.generateCodesDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="count">{t.accessCodes.numberOfCodes}</Label>
              <Input
                id="count"
                type="number"
                min="1"
                max="1000"
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value) || 1)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generateMutation.isPending || count < 1}
            >
              {generateMutation.isPending ? t.common.loading : t.common.create}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invalidate Dialog */}
      <Dialog open={invalidateDialogOpen} onOpenChange={setInvalidateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.accessCodes.invalidate}</DialogTitle>
            <DialogDescription>{t.accessCodes.confirmInvalidate}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvalidateDialogOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleInvalidate}
              disabled={invalidateMutation.isPending}
            >
              {invalidateMutation.isPending ? t.common.loading : t.accessCodes.invalidate}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
